import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core'; // ⭐ NEW: To detect mobile vs web
import api from '../../utils/api';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const { token, user } = useAuth();
    const socketRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    // Stable primitive — `user` is a new object reference on every
    // AuthContext state update even when the underlying user hasn't
    // changed. Depending on the object itself re-triggered this effect
    // mid-handshake, tearing down a socket that hadn't finished connecting
    // yet — that's the "closed before connection is established" warning.
    const userId = user?._id;
    // ⭐ Tracks which user the CURRENTLY CONNECTED socket belongs to. Used to
    // skip a pointless reconnect when this effect re-runs but the logged-in
    // user hasn't actually changed — happens on a hard refresh, where
    // AuthContext hydrates `user` instantly from a cached localStorage
    // profile, connects, then Firebase's own session restore confirms
    // moments later and re-fires with (usually) the same account. Belt and
    // braces alongside the userId dependency and debounce below, in case
    // either firing produces a technically-new value for the same person.
    const activeUserIdRef = useRef(null);

    useEffect(() => {
        const isNative = Capacitor.isNativePlatform();
        let cancelled = false;

        const cleanupSocket = () => {
            if (socketRef.current) {
                console.log('SocketProvider: Cleaning up existing socket.');
                socketRef.current.disconnect();
                socketRef.current = null;
                activeUserIdRef.current = null;
                setIsConnected(false);
            }
        };

        const shouldConnect = isNative
            ? !!(token || localStorage.getItem('token'))
            : !!userId;

        if (!shouldConnect) {
            if (socketRef.current) {
                console.log('SocketProvider: User logged out or token missing. Disconnecting socket.');
                cleanupSocket();
            }
            return cleanupSocket;
        }

        // ⭐ Already connected for this exact user — nothing to do. This is
        // what actually stops the hard-refresh double-connect: the debounce
        // below only helps when the two firings land close together: this
        // check helps regardless of timing, since it compares against
        // whatever socket is live right now rather than racing a timer.
        if (!isNative && socketRef.current && activeUserIdRef.current === userId) {
            return () => { cancelled = true; };
        }

        const connectSocket = () => {
            if (cancelled || socketRef.current) return;

            // ⭐ REVERTED: sockets connect directly to Cloud Run again, not
            // through the Firebase Hosting rewrite. The rewrite can't pass a
            // real WebSocket upgrade through (confirmed: 400 on every attempt),
            // even though plain HTTP through it works fine.
            const SOCKET_URL = process.env.REACT_APP_API_URL;
            if (!SOCKET_URL) {
                console.error("SocketProvider Error: REACT_APP_API_URL is not set.");
                return;
            }

            console.log(`SocketProvider: Attempting to connect to ${SOCKET_URL}...`);
            const newSocket = io(SOCKET_URL, {
                withCredentials: !isNative,
                // ⭐ `auth` as a function, not a static value — Socket.IO calls
                // this fresh before EVERY connection attempt, including its own
                // automatic reconnects (reconnectionAttempts below). A static
                // token object would get reused on every retry, so a dropped
                // connection more than ~60s after the initial connect would
                // silently fail to reconnect once the web socket-token expired.
                // Native: same localStorage token as always (long-lived, so a
                // fresh read each attempt costs nothing but keeps things
                // consistent with web's approach).
                auth: (cb) => {
                    if (isNative) {
                        cb({ token: token || localStorage.getItem('token') });
                    } else {
                        api.get('/auth/socket-token')
                            .then((res) => cb({ token: res.data.socketToken }))
                            .catch((err) => {
                                console.error('SocketProvider: Failed to fetch socket token.', err);
                                cb({ token: null });
                            });
                    }
                },
                transports: ['websocket', 'polling'],
                reconnectionAttempts: 5,
                reconnectionDelay: 3000,
            });

            newSocket.on('connect', () => {
                console.log('✅ WebSocket connected successfully:', newSocket.id);
                activeUserIdRef.current = isNative ? 'native' : userId;
                setIsConnected(true);
            });

            newSocket.on('connect_error', (error) => {
                console.error('❌ WebSocket connection error:', error.message);
                if (error.message.includes('Authentication failed')) {
                     toast.error('Real-time connection failed: Authentication error.');
                }
            });

            newSocket.on('disconnect', (reason) => {
                console.log(`WebSocket disconnected: ${reason}`);
                setIsConnected(false);
            });

            socketRef.current = newSocket;
        };

        connectSocket();

        return () => {
            cancelled = true;
            cleanupSocket();
        };
    }, [token, userId]);

    return (
        <SocketContext.Provider value={socketRef.current}>
            {children}
        </SocketContext.Provider>
    );
};